import { useIntl } from "react-intl";

import type { CampaignSlotCardData } from "@/components/campaign/campaign-slot-card";

import type { ChatGeneratedAsset } from "./assistant-chat-media-gallery-types";

const MIN_PANEL_WIDTH = 292;
const MAX_PANEL_WIDTH = 720;

export function getGenerationStatusLabel(
  status: ChatGeneratedAsset["generationStatus"],
  intl: ReturnType<typeof useIntl>,
) {
  if (status === "done") {
    return intl.formatMessage({ id: "pulse.mediaGallery.status.done", defaultMessage: "Ready" });
  }
  if (status === "failed") {
    return intl.formatMessage({ id: "pulse.mediaGallery.status.failed", defaultMessage: "Failed" });
  }
  if (status === "processing") {
    return intl.formatMessage({
      id: "pulse.mediaGallery.status.processing",
      defaultMessage: "Generating",
    });
  }

  return intl.formatMessage({ id: "pulse.mediaGallery.status.queued", defaultMessage: "Queued" });
}

export function getGeneratedAssetPost(asset: ChatGeneratedAsset): CampaignSlotCardData | null {
  if (!asset.post) {
    return null;
  }

  return {
    id: asset.post.id,
    scheduledFor: asset.post.scheduledFor,
    status: asset.post.status,
    approvalStatus: asset.post.approvalStatus,
    draft: asset.post.draft,
    draftUpdatedAt: asset.post.draftUpdatedAt,
    activeImageRegenerationBlockIds: [],
    slot: asset.post.slot,
    output: asset.post.output
      ? (asset.post.output as CampaignSlotCardData["output"])
      : {
          latestAttempt: null,
          latestCompleted: null,
          latestCopy: null,
        },
  };
}

export function getGeneratedAssetApprovalLabel(
  post: CampaignSlotCardData,
  intl: ReturnType<typeof useIntl>,
) {
  if (post.approvalStatus === "approved") {
    return intl.formatMessage({
      id: "campaignDetail.generatedContentApproval.approved",
      defaultMessage: "Approved",
    });
  }

  if (post.approvalStatus === "rejected") {
    return intl.formatMessage({
      id: "campaignDetail.generatedContentApproval.denied",
      defaultMessage: "Denied",
    });
  }

  return intl.formatMessage({
    id: "campaignDetail.generatedContentApproval.pending",
    defaultMessage: "Pending review",
  });
}

export function getGeneratedAssetCardStatusLabel(
  asset: ChatGeneratedAsset,
  intl: ReturnType<typeof useIntl>,
) {
  if (asset.generationStatus === "done" && asset.scheduledFor) {
    return formatArtifactTimestamp(asset.scheduledFor, intl);
  }

  if (asset.generationStatus !== "failed") {
    return formatArtifactTimestamp(asset.createdAt, intl);
  }

  return getGenerationStatusLabel(asset.generationStatus, intl);
}

export function formatArtifactTimestamp(value: string, intl: ReturnType<typeof useIntl>) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return intl.formatMessage({ id: "pulse.chatHistory.recently", defaultMessage: "Recently" });
  }

  return intl.formatDate(date, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatBytes(bytes: number) {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function clampPanelWidth(value: number) {
  return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, value));
}
